import {useMutation, useQuery} from '@apollo/client'
import {FormEvent, useEffect, useState} from 'react'
import {Row, Col, Form, Spinner} from 'react-bootstrap'
import Select from 'react-select'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import {CREATE_COMMISSION_REPORT} from '../../../gql/mutations/commissionReportMutations'
import {toast} from 'react-toastify'
import {IResCreateCommissionReport, NodeCommissionReport} from '../../../types/commissionReportApi'
import {GET_STATES_EU} from '../../../gql/queries/statesEUQuery'
import {IResStates} from '../../../types/statesEU'
import {GET_ALL_CARRIERS_LIST} from '../../../gql/queries/carriersQuery'
import {IResCarriersList} from '../../../types/carriers'
import {parseId} from '../../../helpers'

interface IModalProps {
  keyboard: boolean
  backdrop: string
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  idFormEdit?: NodeCommissionReport | null
}

const months = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
]

const getYearOptions = () => {
  const currentYear = new Date().getFullYear()
  return Array.from({length: 6}, (_, i) => currentYear - 4 + i)
}

export const ModalAddCommissionReport: React.FC<IModalProps> = ({isOpen, onClose, refetch, idFormEdit}) => {
  const [gqlCreate, {data, loading, error}] = useMutation<IResCreateCommissionReport>(CREATE_COMMISSION_REPORT)
  const {data: dataCarriers} = useQuery<IResCarriersList>(GET_ALL_CARRIERS_LIST, {
    fetchPolicy: 'no-cache',
  })
  const {data: dataStates} = useQuery<IResStates>(GET_STATES_EU)
  
  const [formData, setFormData] = useState({
    name: '',
    effectiveMonth: '',
    effectiveYear: '',
    includePreviousMonth: false,
    approvePreviousYear: false,
    carriers: [] as {value: string; label: string}[],
    states: [] as {value: string; label: string}[],
  })


  useEffect(() => {
    if (!data) return

    const response = data.commissionReportCreateMutation
    if (response?.commissionReport?.id) {
      toast.success('Commission Report added successfully')
      refetch()
      onClose()
    } else if (response?.errors?.length) {
      toast.error(`Error: ${response.errors[0]?.message || 'Something went wrong!'}`)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    if (error) toast.error(`Error: ${error.message}`)
  }, [error])


  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({...prev, [field]: value}))
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!formData.name || !formData.effectiveMonth || !formData.effectiveYear) return

    gqlCreate({
      variables: {
        name: formData.name,
        effectiveMonth: Number(formData.effectiveMonth),
        effectiveYear: Number(formData.effectiveYear),
        carriers: formData.carriers.map(c => parseId(c.value)).filter(id => id !== null),
        statesEU: formData.states.map(state => parseId(state.value)).filter(id => id !== null),
        approvePreviousYear: formData.approvePreviousYear,
      },
    })
  }

  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Create a new commissions report</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <div>
            <Row className='mb-3'>
              <Col md={12}>
                <Form.Label>Report title (required)</Form.Label>
                <Form.Control
                  type='text'
                  placeholder='Enter report title'
                  name='name'
                  required
                  value={formData.name}
                  onChange={e => handleChange('name', e.target.value)}
                />
              </Col>
            </Row>
            <Row className='mb-3'>
              <Col md={6}>
                <Form.Label>Effective month (required)</Form.Label>
                <Form.Select
                  required
                  value={formData.effectiveMonth}
                  onChange={e => handleChange('effectiveMonth', e.target.value)}
                >
                  <option value=''>Select one</option>
                  {months.map((month, index) => (
                    <option key={index} value={index + 1}>{month}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label>Effective year (required)</Form.Label>
                <Form.Select
                  required
                  value={formData.effectiveYear}
                  onChange={e => handleChange('effectiveYear', e.target.value)}
                >
                  <option value=''>Select one</option>
                  {getYearOptions().map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
            <Row className='mb-3'>
              <Col md={12}>
                <Form.Label>Companies to be processed (optional)</Form.Label>
                <Select
                  options={dataCarriers?.allInsuranceOperatorsList.map(carrier => ({
                    value: String(parseId(carrier.id)),
                    label: carrier.name,
                  })) || []}
                  value={formData.carriers}
                  onChange={selected => handleChange('carriers', selected || [])}
                  isMulti
                  isClearable
                />
                <div className='form-text'>
                  You can select multiple carriers (e.g. United One, United One - Vision, United One - Dental). If none selected, the report will be empty.
                </div>
              </Col>
            </Row>
            <Row className='mb-3'>
              <Col md={12}>
                <Form.Label>States to be processed (optional)</Form.Label>
                <Select
                  options={dataStates?.allStates?.edges.map(({node}) => ({
                    value: node.id,
                    label: `${node.name} (${node.code})`,
                  })) || []}
                  value={formData.states}
                  onChange={selected => handleChange('states', selected || [])}
                  isMulti
                  isClearable
                />
                <div className='form-text'>
                  If you do not select any state, policies from all the states will be processed.
                </div>
              </Col>
            </Row>
            <Row className='mb-3'>
              <Col md={12}>
                <Form.Check
                  type='switch'
                  id='approvePreviousYearSwitch'
                  label='Approve policies found in previous year'
                  checked={formData.approvePreviousYear}
                  onChange={e => handleChange('approvePreviousYear', e.target.checked)}
                />
                <div className='form-text'>
                  If enabled, policies found in the previous year will be marked as APPROVED_DISCREPANCY instead of NOT_FOUND.
                </div>
              </Col>
            </Row>

          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button type='button' variant='secondary' onClick={onClose}>
            CLOSE
          </Button>
          <Button type='submit' variant='primary' disabled={loading}>
            {loading && <Spinner animation='border' size='sm' className='me-2' />}
            SUBMIT
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}
